"use client";
import Button, { FormikSubmit } from "@/components/Button";
import ErrorField from "@/components/ErrorField";
import { FieldInput } from "@/components/Input";
import ModalComponent, { ModalHeader, useModal } from "@/components/Modal";
import Spinner from "@/components/icons/Spinner";
import { handleApiErr, useAPI } from "@/lib/api";
import { formatMoney } from "@/lib/locale";
import { cn } from "@/lib/utils";
import {
  CheckIcon,
  ChevronDownIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { Form, Formik } from "formik";
import { TicketCheckIcon, TicketIcon, TrashIcon, XCircle } from "lucide-react";
import Link from "next/link";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import useSWR from "swr";
import * as Yup from "yup";
import cdnUrl from "../vars/cdnUrl";
import { currencyGroups } from "../vars/subscriptions";
import Card from "./Card";
import Dropdown from "./Dropdown";
import { Skeleton } from "./ui/skeleton";

type PaymentItem = {
  subscription_id?: string;
  addon_id?: string;
  value: string;
};

type PaymentModalProps = {
  items: PaymentItem[];
  currency: string;
  modalProp: any;
  voucher?: string;
};

const PaymentModal = (props: PaymentModalProps) => {
  return (
    <ModalComponent
      {...props.modalProp}
      disabled
      className="!max-w-[750px]"
      drawer
      // disabled={panel === "LOADING"}
    >
      <PaymentModalContent {...props} />
    </ModalComponent>
  );
};

const PaymentModalContent = ({
  items,
  modalProp,
  currency: _currency,
  voucher,
}: PaymentModalProps) => {
  const api = useAPI();
  const { t } = useTranslation();
  const timeoutRef = useRef<any>(null);
  const [estimated, setEstimated] = useState<any>(null);
  const availablePayment = useSWR("/subscriptions/available-payment", {
    refreshInterval: 0,
    focusThrottleInterval: 0,
    dedupingInterval: 0,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const [currency, setCurrency] = useState(_currency || "idr");

  const notification = useSWR("/user/notifications/subscription", {
    refreshInterval: 0,
    focusThrottleInterval: 0,
    dedupingInterval: 0,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const hasPendingInvoice = useMemo(() => {
    if (typeof notification?.data?.data?.has_pending_invoice !== "undefined") {
      return notification.data.data.has_pending_invoice;
    }
    return null;
  }, [notification.data]);

  const [panel, setPanel] = useState("LOADING");
  const [loadingState, setLoadingState] = useState<
    "PAYMENT" | "CHANGE_PAYMENT" | "IDLE"
  >("IDLE");
  const [error, setError] = useState<any>({});

  const [selectedPayment, setSelectedPayment] = useState<string | null>(
    currency === "usd" ? "PAYPAL" : "QRIS"
  );

  const [voucherCode, setVoucherCode] = useState<any>(voucher);

  const onProcess = async () => {
    setLoadingState("PAYMENT");
    setPanel("LOADING");
    try {
      const data = await api.post("/user/invoices/create", {
        payment_method: selectedPayment || "QRIS",
        items,
        currency,
        voucher: voucherCode,
      });
      window.open(data.data.invoice_url, "_self");
    } catch (e) {
      handleApiErr(e, { toast, t });
      setLoadingState("IDLE");
      setPanel("IDLE");
    }
  };

  const redeemModal = useModal();

  const onEstimate = async (items, selectedPayment, voucherCode, currency) => {
    setLoadingState("CHANGE_PAYMENT");
    setError(null);
    try {
      const data = await api.post("/user/invoices/estimate", {
        items,
        payment_method: selectedPayment || "QRIS",
        currency,
        voucher: voucherCode,
      });
      setEstimated(data.data);
    } catch (e) {
      handleApiErr(e, { toast, t });
      setError(e.response?.data);
      setEstimated(null);
    }
    setLoadingState("IDLE");
    setPanel("IDLE");
  };

  useEffect(() => {
    setLoadingState("CHANGE_PAYMENT");
    if (items.length > 0) {
      timeoutRef.current = setTimeout(() => {
        onEstimate(items, selectedPayment, voucherCode, currency);
      }, 100);
    }
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [items, selectedPayment, voucherCode, currency]);

  return (
    <>
      <ModalHeader {...modalProp} disabled={panel === "LOADING"}>
        <Trans i18nKey="confirm_payment">Confirm Payment</Trans>
      </ModalHeader>

      <div className="flex flex-wrap -mx-6">
        <div className="px-6 md:border-r w-full md:w-6/12">
          <p>
            <Trans i18nKey="purchase_subs_desc">
              You&apos;re going to purchase subscription with detailed below:
            </Trans>
          </p>

          {loadingState === "CHANGE_PAYMENT" ? (
            <div className="flex flex-wrap -m-2 py-4">
              <div className="p-2 w-8/12">
                <Skeleton className="w-9/12 h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-8/12">
                <Skeleton className="w-11/12 h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-8/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-8/12">
                <Skeleton className="w-6/12 h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-8/12">
                <Skeleton className="w-10/12 h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
              <div className="p-2 w-8/12">
                <Skeleton className="w-8/12 h-4" />
              </div>
              <div className="p-2 w-4/12">
                <Skeleton className="w-full h-4" />
              </div>
            </div>
          ) : error !== null ? (
            <div className="flex flex-col items-center mt-8 text-center">
              <XCircleIcon className="w-8 text-red-500" />
              {error?.message === "not_acceptable_currency" ? (
                <h2 className="mt-2 font-semibold">
                  <Trans i18nKey="select_other_payment_method">
                    Please select other payment method
                  </Trans>
                </h2>
              ) : error?.message === "cookie token is empty" ? (
                <h2 className="mt-2 font-semibold">
                  <Trans i18nKey="login_to_continue">
                    Please login to continue
                  </Trans>
                </h2>
              ) : (
                <h2 className="mt-2 font-semibold">
                  <Trans i18nKey="invalid_payment_data">
                    Invalid Payment Data
                  </Trans>
                </h2>
              )}
            </div>
          ) : (
            <table className="mt-2 w-full text-sm">
              {estimated?.items?.map((item, index) => (
                <tr key={index}>
                  <td
                    className={cn(
                      "pt-1 font-semibold",
                      index === items.length - 1 ? "pb-2" : ""
                    )}
                  >
                    {item.name}
                  </td>
                  <td
                    className={cn(
                      "pt-1 text-right",
                      index === items.length - 1 ? "pb-2" : ""
                    )}
                  >
                    {formatMoney(item?.amount, currency)}
                  </td>
                </tr>
              ))}
              <tr className="">
                <td className="pt-1 pb-2 border-t border-b font-semibold align-top">
                  <p>
                    <Trans i18nKey="discount">Discount</Trans>
                  </p>
                  {estimated.discount_description ? (
                    <p className="text-green-600 text-xs">
                      {estimated.discount_description}{" "}
                      {estimated.discount_percentage * 100}%
                    </p>
                  ) : (
                    <Fragment />
                  )}
                </td>
                <td className="pt-1 pb-2 border-t border-b text-right truncate align-top">
                  {formatMoney(
                    estimated?.discount > 0 ? -estimated?.discount : 0,
                    currency
                  )}
                </td>
              </tr>
              <tr>
                <td className="pt-1 font-semibold">
                  <Trans i18nKey="sub_total">Sub Total</Trans>
                </td>
                <td className="pt-1 text-right truncate">
                  {formatMoney(estimated?.total, currency)}
                </td>
              </tr>
              <tr>
                <td className="pt-1 font-semibold">
                  <Trans i18nKey="tax">Tax</Trans> (11%)
                </td>
                <td className="pt-1 text-right truncate">
                  {formatMoney(estimated?.tax_amount, currency)}
                </td>
              </tr>
              <tr>
                <td className="pt-1 pb-2 font-semibold">
                  <Trans i18nKey="transaction_fee">Transaction Fee</Trans>
                </td>
                <td className="pt-1 pb-2 text-right truncate">
                  {formatMoney(estimated?.transaction_fee, currency)}
                </td>
              </tr>
              <tr>
                <td className="pt-1 border-t font-semibold">
                  <Trans i18nKey="grand_total">Grand Total</Trans>
                </td>
                <td className="pt-1 border-t font-semibold text-lg text-right truncate">
                  {formatMoney(estimated?.grand_total, currency)}
                </td>
              </tr>
            </table>
          )}
        </div>

        <div className="px-6 w-full md:w-6/12">
          <div className="mt-4 md:mt-0 mb-8">
            <h2 className="mb-2 font-montserrat font-semibold">
              <Trans i18nKey="select_payment_method">
                Select payment method
              </Trans>
            </h2>

            <div className="flex flex-wrap -m-1">
              {availablePayment.isLoading ? (
                <>
                  <div className="bg-gray-300 mt-4 rounded w-6/12 h-4" />
                  <div className="flex gap-3 mt-3 mb-3 w-full">
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                  </div>

                  <div className="bg-gray-300 mt-4 rounded w-6/12 h-4" />
                  <div className="flex gap-3 mt-3 mb-3 w-full">
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                  </div>

                  <div className="bg-gray-300 mt-4 rounded w-6/12 h-4" />
                  <div className="flex gap-3 mt-3 mb-3 w-full">
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                    <div className="bg-gray-300 rounded w-4/12 h-11" />
                  </div>
                </>
              ) : availablePayment.error ? (
                <></>
              ) : availablePayment.data ? (
                <>
                  <div className="flex justify-end items-center mb-3 w-full text-right">
                    <p className="mr-3">
                      <Trans i18nKey="currency">Currency</Trans>
                    </p>
                    <CurrencyChooser
                      value={currency}
                      onChange={(key) => {
                        if (key === "idr") {
                          setSelectedPayment("QRIS");
                        }
                        if (key === "usd") {
                          setSelectedPayment("PAYPAL");
                        }
                        setCurrency(key);
                      }}
                    />
                  </div>

                  {currency === "idr" && (
                    <>
                      <div className="relative mb-3 p-3 border rounded-md">
                        <p className="top-0 right-0 absolute bg-orange-500 px-2 py-1 rounded-md font-semibold text-white text-xs">
                          <Trans i18nKey="recommended">Recommended</Trans>
                        </p>
                        <RenderPaymentLists
                          lists={availablePayment.data?.data}
                          title="QR Code"
                          category="QRCODE"
                          itemClassName="w-full"
                          onSelected={(name) => {
                            setSelectedPayment(name);
                          }}
                          selectedPayment={selectedPayment}
                          currency={currency}
                        />
                        <p className="w-full text-foreground/50 text-sm">
                          <Trans i18nKey="qris_payment_benefit">
                            *Less Transaction Fee With{" "}
                            <strong>QRIS Payment</strong>, supports:{" "}
                            <strong>DANA</strong>, <strong>GoPay</strong>,{" "}
                            <strong>BCA Mobile</strong>, <strong>OVO</strong>,{" "}
                            <strong>LinkAja</strong> and any platform that
                            supports QRIS payment.
                          </Trans>
                        </p>
                      </div>

                      <RenderPaymentLists
                        lists={availablePayment.data?.data}
                        title="E-Wallet"
                        category="EWALLET"
                        itemClassName="w-4/12"
                        onSelected={(name) => {
                          setSelectedPayment(name);
                        }}
                        selectedPayment={selectedPayment}
                        currency={currency}
                      />

                      <RenderPaymentLists
                        lists={availablePayment.data?.data}
                        title="Virtual Account"
                        category="VIRTUAL_ACCOUNT"
                        itemClassName="w-4/12"
                        onSelected={(name) => {
                          setSelectedPayment(name);
                        }}
                        selectedPayment={selectedPayment}
                        currency={currency}
                      />

                      <RenderPaymentLists
                        lists={availablePayment.data?.data}
                        title="Credit Card"
                        category="CREDIT_CARD"
                        itemClassName="w-full"
                        onSelected={(name) => {
                          setSelectedPayment(name);
                        }}
                        selectedPayment={selectedPayment}
                        currency={currency}
                      />
                    </>
                  )}

                  {currency === "usd" && (
                    <>
                      <RenderPaymentLists
                        lists={availablePayment.data?.data}
                        title="Other"
                        category="PAYPAL"
                        itemClassName="w-full"
                        onSelected={(name) => {
                          setSelectedPayment(name);
                        }}
                        selectedPayment={selectedPayment}
                        currency={currency}
                      />
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className="mt-4">
            {voucherCode !== null ? (
              <div className="flex justify-center items-center mb-4 border rounded-md w-full">
                <p className="flex flex-1 items-center p-3 text-sm truncate">
                  <TicketCheckIcon className="size-4" />
                  <div className="flex-1 pl-2 truncate">
                    <Trans i18nKey="voucher_code_used">Voucher Code Used</Trans>
                    : <strong>{voucherCode}</strong>
                  </div>
                </p>
                <button
                  className="mr-1 p-2"
                  onClick={() => {
                    setVoucherCode(null);
                  }}
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                className="flex justify-center items-center mb-4 w-full text-center"
                onClick={redeemModal.open}
              >
                <TicketIcon className="mr-2 w-5" />
                <Trans i18nKey="use_voucher_code">Use Voucher Code</Trans>
              </Button>
            )}

            {selectedPayment === null && (
              <p className="mb-1 text-red-500 text-sm">
                *
                <Trans i18nKey="select_payment_first">
                  Please select payment method first
                </Trans>
              </p>
            )}
            {hasPendingInvoice === true ? (
              <>
                <p className="bg-blue-50 p-3 text-sm text-center">
                  <Trans i18nKey="purchase_subs_already">
                    You already have an active subscription. Please cancel
                    current subscription to change the package.
                  </Trans>
                </p>
                <Link href="/dashboard/subscription">
                  <Button
                    variant="blue"
                    className="flex justify-center items-center mt-3 w-full"
                  >
                    <Trans i18nKey="go_to_subscription">
                      Go to subscription
                    </Trans>
                  </Button>
                </Link>
              </>
            ) : (
              <Button
                variant="blue"
                className="flex justify-center items-center w-full"
                disabled={
                  hasPendingInvoice ||
                  loadingState !== "IDLE" ||
                  selectedPayment === null ||
                  error !== null
                }
                onClick={onProcess}
              >
                {loadingState === "PAYMENT" && (
                  <Spinner className="mr-3 w-5 animate-spin" />
                )}{" "}
                {loadingState === "PAYMENT"
                  ? t("process_payment_dont_close")
                  : t("process_payment")}
              </Button>
            )}
            <Button
              variant="secondary"
              className="mt-2 w-full"
              onClick={() => {
                if (loadingState === "IDLE") modalProp.close();
              }}
              disabled={loadingState !== "IDLE"}
            >
              Cancel
            </Button>
          </div>

          {currency === "idr" && (
            <div className="mt-3 text-gray-500 text-xs text-center">
              <Trans i18nKey="payment_gateway_powered">
                Payment gateway powered by{" "}
                <a
                  href="https://xendit.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Xendit
                </a>
              </Trans>
            </div>
          )}
        </div>
      </div>
      <ModalComponent {...redeemModal} drawer>
        <ModalHeader {...redeemModal}>
          <Trans i18nKey="voucher_code">Voucher Code</Trans>
        </ModalHeader>
        <CheckRedeemCode
          modalProp={redeemModal}
          items={items}
          onChange={(value) => {
            setVoucherCode(value);
          }}
        />
      </ModalComponent>
    </>
  );
};

const CurrencyChooser = ({ value, onChange }) => {
  const selected = currencyGroups[value || "idr"];

  return (
    <Dropdown.Menu
      head={
        <div className="px-3 py-1 border rounded text-sm">
          {selected.label}{" "}
          <ChevronDownIcon className="inline-block -mt-0.5 w-4" />
        </div>
      }
    >
      {Object.entries(currencyGroups).map(([key, item]) => (
        <Dropdown.Item
          key={key}
          className={cn(
            "text-sm",
            value === key ? "!bg-blue-600 !text-white" : ""
          )}
          onClick={() => onChange(key)}
        >
          {item.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  );
};

const CheckRedeemCode = ({ modalProp, items, onChange }) => {
  const api = useAPI();
  const { t } = useTranslation();
  const [isFound, setIsFound] = useState<any>(null);
  const [errMsg, setErrMsg] = useState("");
  const [code, setCode] = useState("");
  const [percentage, setPercentage] = useState(0);

  const onSubmitCode = async (values) => {
    setIsFound(null);

    try {
      const { data } = await api.post("/user/invoices/voucher", {
        code: values.code,
        items,
      });
      setIsFound(true);
      setPercentage(data.discount);
      setCode(values.code);
    } catch (e) {
      handleApiErr(e, {
        toast,
        t,
        onError: (msg) => {
          const msgs = [
            "voucher_not_found",
            "voucher_already_used",
            "voucher_not_available_for_this_package",
          ];
          if (msgs.includes(msg)) {
            setIsFound(false);
            setErrMsg(msg);
            return true;
          }
          return false;
        },
      });
    }
  };

  return (
    <Fragment>
      <Formik
        initialValues={{ code: "" }}
        validationSchema={Yup.object().shape({
          code: Yup.string().required(
            t("validation_required", { field: t("voucher_code") })
          ),
        })}
        onSubmit={onSubmitCode}
      >
        {({ isSubmitting }) => (
          <Form className="pb-10 sm:pb-0">
            <p className="mb-2">
              <Trans i18nKey="type_voucher_code">Type Voucher code</Trans>
            </p>
            <div className="relative mt-2">
              <FieldInput
                type="text"
                name="code"
                className="w-full"
                placeholder={t("code")}
                inputSize="lg"
                required
                disabled={isSubmitting}
              />
              <div className="top-0 right-1.5 bottom-0 absolute flex items-center">
                <FormikSubmit
                  size="sm"
                  variant="secondary"
                  className="flex text-sm"
                >
                  <CheckIcon className="mr-1 w-4" />{" "}
                  <Trans i18nKey="check">Check</Trans>
                </FormikSubmit>
              </div>
            </div>
            <ErrorField name="code" />

            {isFound === false && (
              <Card className="flex items-center mt-4 p-3">
                <XCircle className="w-6 text-red-500" />
                <p className="ml-2 font-medium text-foreground/60">
                  {t(errMsg)}
                </p>
              </Card>
            )}

            {isFound === true && (
              <Card className="flex items-center mt-4 p-3 border border-blue-500 ring-4 ring-blue-500/20">
                <TicketIcon className="size-6 text-blue-500 -rotate-45" />
                <div className="flex-1 ml-2">
                  <p className="font-semibold">
                    <Trans i18nKey="voucher_code_available">
                      Voucher Code is available
                    </Trans>
                  </p>
                  <p className="text-foreground/50 text-sm">
                    {code} - ({percentage}%)
                  </p>
                </div>
                <div>
                  <Button
                    variant="blue"
                    className="text-sm"
                    type="button"
                    onClick={() => {
                      modalProp.close();
                      onChange(code);
                    }}
                  >
                    <Trans i18nKey="use_code">Use Code</Trans>
                  </Button>
                </div>
              </Card>
            )}
          </Form>
        )}
      </Formik>
    </Fragment>
  );
};

const RenderPaymentLists = ({
  lists,
  title,
  category,
  itemClassName,
  onSelected,
  selectedPayment,
  currency,
}) => {
  return (
    <>
      <h2 className="mb-1 px-1 w-full font-semibold text-foreground text-sm">
        {title}
      </h2>

      {lists
        ?.filter((m) => m.category === category)
        ?.map((item, index) => {
          return (
            <PaymentMethodBtn
              key={index}
              onClick={() => {
                if (item.name === selectedPayment) return;
                onSelected(item.name);
              }}
              selected={item.name === selectedPayment}
              icon={item.image}
              label={item.name}
              className={itemClassName}
              disabled={item.accepts[currency] === false}
            />
          );
        })}
    </>
  );
};

const PaymentMethodBtn = ({
  onClick,
  selected,
  label,
  icon,
  className,
  disabled = false,
}) => {
  return (
    <div className={cn(className, "px-1")}>
      <button
        onClick={onClick}
        disabled={disabled}
        type="button"
        className={cn(
          "transition-all",
          "p-1  border rounded-md flex items-center justify-center mb-2 w-full text-center",
          disabled
            ? "bg-slate-100 opacity-50"
            : [
                "bg-white hover:bg-slate-50",
                selected
                  ? "border-green-500 ring-4 ring-green-500/30 !bg-green-50"
                  : "",
              ]
        )}
      >
        <img
          src={cdnUrl + "/images/payments/" + icon}
          height={30}
          alt={label}
        />
      </button>
    </div>
  );
};

export default PaymentModal;
